import type {
  FormatCompactCurrencyOptions,
  FormatCurrencyOptions,
  NumericInput,
  SubscriptCurrencyParts,
} from "~/lib/display-format";
import {
  formatCompactCurrency,
  formatCurrency,
  formatQuantity,
  formatSubscriptCurrencyParts,
  toFiniteNumber,
} from "~/lib/display-format";
import { formatUsd } from "~/lib/formatters";
import type { DisplayCurrency } from "./denomination-lens";
import { formatMarketPrice } from "./market-value-format";

type MarketUsdInput = number | string | null | undefined;

/**
 * The rendering seam for public market data such as prices, quotes, market
 * caps, and volumes. This formatter is intentionally privacy-blind: market
 * data is the same for every user and must never be hidden by the privacy
 * lens. Quantities represent asset units rather than currency and therefore
 * never receive FX conversion.
 */
export interface MarketValueFormatter {
  compactCurrency(
    value: NumericInput,
    options?: FormatCompactCurrencyOptions,
  ): string;
  currency(value: NumericInput, options?: FormatCurrencyOptions): string;
  price(value: NumericInput, options?: { fallback?: string }): string;
  quantity(value: NumericInput, options?: { fallback?: string }): string;
  subscriptCurrencyParts(
    value: NumericInput,
    options?: FormatCurrencyOptions,
  ): SubscriptCurrencyParts;
  usd(value: MarketUsdInput): string;
}

export function createMarketValueFormatter(input: {
  denomination?: {
    currency: DisplayCurrency;
    rate: number;
  };
}): MarketValueFormatter {
  const { denomination } = input;

  if (denomination === undefined || denomination.currency === "USD") {
    return {
      compactCurrency: (value, options) =>
        formatCompactCurrency(value, options),
      currency: (value, options) => formatCurrency(value, options),
      price: (value, options) => formatMarketPrice(value, options),
      quantity: (value, options) => formatQuantity(value, options),
      subscriptCurrencyParts: (value, options) =>
        formatSubscriptCurrencyParts(value, options),
      usd: (value) => formatUsd(value),
    };
  }

  const convert = (value: NumericInput): number | null => {
    const numericValue = toFiniteNumber(value);
    return numericValue === null ? null : numericValue * denomination.rate;
  };
  const withDenomination = (
    options?: FormatCurrencyOptions,
  ): FormatCurrencyOptions => ({
    ...options,
    currency: denomination.currency,
  });

  return {
    compactCurrency: (value, options) =>
      formatCompactCurrency(convert(value), withDenomination(options)),
    currency: (value, options) =>
      formatCurrency(convert(value), withDenomination(options)),
    price: (value, options) =>
      formatMarketPrice(convert(value), {
        ...options,
        currency: denomination.currency,
      }),
    quantity: (value, options) => formatQuantity(value, options),
    subscriptCurrencyParts: (value, options) =>
      formatSubscriptCurrencyParts(convert(value), withDenomination(options)),
    usd: (value) =>
      formatCurrency(convert(value), {
        currency: denomination.currency,
      }),
  };
}
